import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { EXPERTISES_DATA } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { useSEO } from "@/lib/useSEO";
import { BookingButton } from "@/components/BookingButton";

export default function ExpertiseDetail() {
  const { slug } = useParams();
  const idx = EXPERTISES_DATA.findIndex((e) => e.slug === slug);
  const e = EXPERTISES_DATA[idx];

  useSEO({
    title: e ? e.title : "Expertise",
    description: e ? `${e.title} — ${e.intro}` : "",
    keywords: e ? `${e.title}, ${e.points.join(", ")}, HERUS Axiom` : "",
    path: `/expertise/${slug}`,
  });

  if (!e) return <Navigate to="/expertises" replace />;

  const others = EXPERTISES_DATA.filter((x) => x.slug !== slug);

  return (
    <div data-testid="expertise-detail-page" className="bg-[#0A0A0A]">
      {/* Hero */}
      <section className="pt-40 lg:pt-52 pb-16 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal>
          <Link to="/expertises" data-testid="back-to-expertises" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/50 hover:text-[#C5A059] transition-colors">
            <ArrowRight size={14} className="rotate-180" /> Expertises
          </Link>
        </Reveal>
        <div className="mt-8 flex items-baseline gap-6">
          <span className="font-serif-display text-5xl lg:text-7xl text-[#C5A059] leading-none">{e.num}</span>
          <Reveal><Overline>Domaine d'expertise</Overline></Reveal>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-6 font-serif-display font-light leading-[0.95] tracking-tight text-[#F9F6F0] text-4xl sm:text-5xl lg:text-7xl max-w-5xl"
        >
          {e.title}
        </motion.h1>
        <Reveal i={2}>
          <p className="mt-8 max-w-3xl font-serif-display text-2xl lg:text-3xl font-light italic text-[#C5A059]">{e.lead}</p>
        </Reveal>
        <Reveal i={3}>
          <p className="mt-8 max-w-2xl text-[#F9F6F0]/70 text-base lg:text-lg font-light leading-relaxed">{e.intro}</p>
        </Reveal>
      </section>

      {/* Full-bleed image */}
      <section className="relative h-[45vh] min-h-[320px] overflow-hidden border-y border-white/10">
        <img src={e.img} alt={e.alt} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/30" />
      </section>

      {/* Body + expertises clés */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-7 space-y-8">
          {e.body.map((p, i) => (
            <Reveal key={i} i={i}>
              <p className="text-[#F9F6F0]/70 text-lg font-light leading-relaxed">{p}</p>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">Expertises clés</p>
          </Reveal>
          <ul className="mt-8 border-t border-white/10">
            {e.points.map((p, i) => (
              <Reveal key={p} i={i % 3} className="flex items-center gap-4 border-b border-white/10 py-4">
                <span className="h-1.5 w-1.5 bg-[#C5A059] rotate-45 shrink-0" />
                <span className="text-[#F9F6F0]/85">{p}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Other expertises — internal linking */}
      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20">
          <Reveal><Overline>Poursuivre</Overline></Reveal>
          <div className="mt-10 grid md:grid-cols-3 border-t border-l border-white/10">
            {others.map((o, i) => (
              <Reveal key={o.slug} i={i % 3}>
                <Link
                  to={`/expertise/${o.slug}`}
                  data-testid={`related-${o.slug}`}
                  className="group block h-full border-b border-r border-white/10 p-8 hover:bg-white/5 transition-colors duration-500"
                >
                  <span className="font-serif-display text-3xl text-[#C5A059]">{o.num}</span>
                  <h3 className="mt-4 font-serif-display text-xl text-[#F9F6F0] group-hover:text-[#C5A059] transition-colors">{o.navLabel}</h3>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40 group-hover:text-[#C5A059] transition-colors">
                    Découvrir <ArrowUpRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-white/10 py-28 lg:py-36 px-6 bg-[#0A0A0A] overflow-hidden">
        <ParticleField opacity={1.3} />
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <Reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.2] text-[#F9F6F0]">
              Un projet relevant de <span className="italic text-[#C5A059]">{e.navLabel.toLowerCase()}</span> ?
            </h2>
          </Reveal>
          <Reveal i={1} className="mt-10 flex flex-wrap items-center justify-center gap-8">
            <Link to="/contact" data-testid="expertise-detail-cta" className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-[#F9F6F0]/80 hover:text-[#C5A059] border-b border-white/20 hover:border-[#C5A059] pb-2 transition-colors duration-500">
              Parlez-nous de votre projet
              <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <BookingButton testId="expertise-detail-booking-btn" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
